import {
  saveNotes,
  fetchNotes,
} from '../../../packages/neb-api-client/src/notes';
import {
  openError,
  openSuccess,
} from '../../../packages/neb-dialog/neb-banner-state';
import { store } from '../../../packages/neb-redux/neb-redux-store';
import {
  FEATURE_FLAGS,
  getFeatures,
} from '../../../packages/neb-utils/feature-util';
import { UpdateNotificationService } from '../../services/update-notifications';
import { updated, ANY } from '../../utils/update-notifications';

import { getUserEmailContext } from './utils';

export class AmbientScribeReactiveController {
  constructor(host) {
    this.host = host;
    this.widget = null;
    this.widgetLoaded = false;
    this.widgetReady = false;

    this.pendingContext = {
      encounterId: null,
      appointmentData: null,
    };

    // Set up subscription to encounter context updates
    this.__contextNotificationService = new UpdateNotificationService({
      defaultQuery: {
        ambientScribeContext: ANY,
      },
      callback: data => {
        if (data && data.ambientScribeContext) {
          this.setContext(data.ambientScribeContext);
        }
      },
    });

    // Only add controller if host exists
    if (host) {
      host.addController(this);
    }
  }

  hostConnected() {
    this.__contextNotificationService.connect();
  }

  hostDisconnected() {
    this.__contextNotificationService.disconnect();

    if (this.widget && this.widget.isMounted()) {
      this.widget.unmount();
    }
  }

  // Simplified Public API
  async toggleWidget() {
    try {
      await this.__ensureWidgetReady();

      if (!this.widget.isMounted()) {
        this.widget.mount();

        this.__applyPendingContext();
      } else if (!this.widget.isVisible() && this.widget.isMounted()) {
        this.widget.show();
      } else {
        this.widget.unmount();
        this.widgetReady = false;
      }

      // Dispatch a custom event for components that need to update their UI
      window.dispatchEvent(
        new CustomEvent('ambient-scribe-widget-toggled', {
          detail: {
            widgetMounted: this.widget.isMounted(),
            widgetReady: this.widgetReady,
          },
        }),
      );
    } catch (error) {
      console.error('Error toggling ambient scribe widget:', error);
    }
  }

  // Simplified context setting - handles both scenarios
  setContext(contextData) {
    // Always update pending context
    this.pendingContext = { ...this.pendingContext, ...contextData };

    // If widget is loaded, mounted, and ready, apply immediately
    if (this.widget && this.widget.isMounted() && this.widgetReady) {
      this.__applyContextToWidget(contextData);
    }
  }

  // Convenience method for encounter
  setEncounter(encounterId) {
    this.setContext({ encounterId });
  }

  // Private methods
  async __ensureWidgetReady() {
    if (this.widgetLoaded) return;

    await this.__loadWidget();
    this.widgetLoaded = true;
  }

  async __loadWidget() {
    if (this.widget) return;

    // Load script if needed
    if (!window.AmbientScribeWidget) {
      await this.__loadScript();
    }

    // Load feature flags
    const features = await getFeatures();
    const showTemplateScreen = features.includes(
      FEATURE_FLAGS.AS_AMBIENT_SCRIBE_TEMPLATE,
    );
    const showHomeScreen = features.includes(
      FEATURE_FLAGS.AS_AMBIENT_SCRIBE_HOME,
    );

    // Create widget
    this.widget = new window.AmbientScribeWidget({
      id: 'ambient-scribe-widget',
      initialPosition: { x: 50, y: 50 },
      initialSize: { width: '30%', height: '50%' },
      capabilities: {
        canToggleFullscreen: true,
        draggable: true,
        resizable: true,
        allowClose: true, // temporary until we expose close events from widget
      },
      initialContext: {
        features: { showHomeScreen, showTemplateScreen },
        ...getUserEmailContext(),
      },
    });

    // Set up widget event listeners
    this.widget.on('exportDocument', this.__handleDocumentExport.bind(this));
    this.widget.on('ready', this.__handleWidgetReady.bind(this));

    // Global reference for debugging
    window.scribe = this.widget;
  }

  __loadScript() {
    const script = document.createElement('script');
    script.src = window.NEB_ENV.AMBIENT_SCRIBE_URL;
    script.async = true;

    return new Promise((resolve, reject) => {
      script.onload = () => {
        if (!window.AmbientScribeWidget) {
          reject(new Error('AmbientScribeWidget not found after script load'));
          return;
        }

        resolve();
      };

      script.onerror = event => {
        const errorMsg = event.error
          ? event.error.message
          : `Failed to load script from ${script.src}`;
        reject(new Error(errorMsg));
      };

      document.head.appendChild(script);
    });
  }

  __applyPendingContext() {
    const hasValidContext = Object.keys(this.pendingContext).some(
      key => this.pendingContext[key] !== null,
    );

    if (hasValidContext && this.widgetReady) {
      this.__applyContextToWidget(this.pendingContext);
    }
  }

  __handleWidgetReady() {
    this.widgetReady = true;

    // Apply any pending context now that widget is ready
    this.__applyPendingContext();
  }

  __applyContextToWidget(contextData) {
    if (contextData.encounterId && contextData.appointmentData) {
      this.widget.emit('setEncounter', {
        appointment: {
          id: contextData.encounterId,
          patientName: contextData.appointmentData.patientName,
          title: contextData.appointmentData.title,
        },
      });
    }
  }

  async __handleDocumentExport(data) {
    const { document } = data;

    if (document && document.encounterId) {
      try {
        const parsedNotes = this.__parseDocumentContent(document);

        await this.__saveNotesWithLogging(
          parsedNotes,
          this.pendingContext.encounterId,
        );

        window.dispatchEvent(new CustomEvent('neb-scribe-refetch-notes'));
        this.__notifyDocumentExported(data);
      } catch (error) {
        console.error('Error saving ambient scribe notes:', error);

        this.__logDocumentExportError(data, error);
      }
    }
  }

  __parseDocumentContent(document) {
    const { type, content } = document;

    switch (type) {
      case 'chart_note':
        return this.__parseSOAPContent(content);

      // Add more document types here as needed
      // case 'procedure_note':
      //   return this.__parseProcedureContent(content);
      // case 'discharge_summary':
      //   return this.__parseDischargeContent(content);

      default:
        console.warn('[AMBIENT SCRIBE] Unknown document type:', type);
        return {};
    }
  }

  __parseSOAPContent(content) {
    const notes = {};

    if (typeof content === 'object' && content !== null) {
      ['subjective', 'objective', 'assessment', 'plan'].forEach(key => {
        if (content[key]) {
          const cleanContent = content[key]
            .trim()
            .split('\n')
            .filter(line => line.trim())
            .map(line => `<p>${line.trim()}</p>`)
            .join('');

          // Apply font styling to the parsed content
          notes[key] = this.__applyFontStyling(cleanContent);
        }
      });

      return notes;
    }

    console.warn('[AMBIENT SCRIBE] Unexpected content format:', typeof content);
    return notes;
  }

  __logDocumentExportError(data, error) {
    const timestamp = new Date().toISOString();

    console.group(`❌ [AMBIENT SCRIBE] Document Export Error - ${timestamp}`);

    console.error('💥 Error Details:', {
      timestamp,
      errorMessage: error.message,
      errorStack: error.stack,
      encounterId: this.pendingContext.encounterId,
      documentData: data.document || 'No document data',
    });

    console.error('🗂️ Failed Export Data:', JSON.stringify(data, null, 2));

    console.groupEnd();
  }

  __mergeNotes(prevNotes, newNotes) {
    const mergedNotes = {};
    const soapSections = ['subjective', 'objective', 'assessment', 'plan'];

    soapSections.forEach(section => {
      mergedNotes[section] = this.__mergeSectionContent(
        prevNotes?.[section] || '',
        newNotes?.[section] || '',
      );
    });

    return mergedNotes;
  }

  __mergeSectionContent(prevContent, newContent) {
    // Clean empty paragraph tags from both contents
    const cleanPrevContent = this.__cleanEmptyParagraphs(prevContent);
    const cleanNewContent = this.__cleanEmptyParagraphs(newContent);

    // Apply font styling to previous content if it exists and doesn't already have styling
    const styledPrevContent = cleanPrevContent
      ? this.__ensureFontStyling(cleanPrevContent)
      : '';

    return this.__combineStyledContent(styledPrevContent, cleanNewContent);
  }

  __combineStyledContent(styledPrevContent, cleanNewContent) {
    const hasNewContent = this.__hasActualContent(cleanNewContent);

    if (styledPrevContent && hasNewContent) {
      // Both have content - append new to existing with line break
      return `${styledPrevContent}<br>${cleanNewContent}`;
    }

    if (styledPrevContent) {
      // Only previous content exists
      return styledPrevContent;
    }

    if (hasNewContent) {
      // Only new content exists
      return cleanNewContent;
    }

    // Neither has content
    return '';
  }

  __hasActualContent(content) {
    return content && content.trim();
  }

  __cleanEmptyParagraphs(content) {
    if (!content || typeof content !== 'string') {
      return '';
    }

    // Remove empty paragraphs with non-breaking spaces and empty paragraphs
    // Also trim any leading/trailing whitespace
    return content
      .replace(/<p>&nbsp;<\/p>/g, '')
      .replace(/<p><\/p>/g, '')
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/^\s+|\s+$/g, '')
      .trim();
  }

  __applyFontStyling(content) {
    if (!content || typeof content !== 'string') {
      return content;
    }

    const { fontFamily, fontSize } = this.__getProviderFontSettings();

    if (!fontFamily && !fontSize) {
      return content;
    }

    // Apply font styling similar to CKEditor utilities
    let styledContent = content;

    // Apply font styles to paragraphs
    styledContent = this.__addFontStylesToParagraphs(
      styledContent,
      fontFamily,
      fontSize,
    );

    // Apply font styles to tables if present
    styledContent = this.__addFontStylesToTables(
      styledContent,
      fontFamily,
      fontSize,
    );

    // Apply font styles to list items if present
    styledContent = this.__addFontStylesToListItems(
      styledContent,
      fontFamily,
      fontSize,
    );

    return styledContent;
  }

  __getProviderFontSettings() {
    const defaultSettings = {
      fontFamily: 'Helvetica',
      fontSize: '16',
    };

    try {
      const provider = this.__findProviderFromContext();

      if (provider) {
        return this.__extractFontSettingsFromProvider(
          provider,
          defaultSettings,
        );
      }
    } catch (error) {
      console.warn(
        '[AMBIENT SCRIBE] Could not get provider font settings:',
        error,
      );
    }

    return defaultSettings;
  }

  __findProviderFromContext() {
    const { appointmentData } = this.pendingContext;

    if (!appointmentData?.providerId) {
      return null;
    }

    const providers = store.getState().providers?.item || [];
    return providers.find(p => p.id === appointmentData.providerId);
  }

  __extractFontSettingsFromProvider(provider, defaultSettings) {
    const fontFamily =
      provider.richTextFontFamily || defaultSettings.fontFamily;
    const fontSize = provider.richTextFontSize || defaultSettings.fontSize;

    return {
      fontFamily: fontFamily.toString(),
      fontSize: fontSize.toString(),
    };
  }

  __addFontStylesToParagraphs(content, fontFamily, fontSize) {
    const fontFamilyString = fontFamily.includes(' ')
      ? `'${fontFamily}'`
      : fontFamily;

    return content.replace(/<p>(.*?)<\/p>/g, (paragraph, oldContent) => {
      if (!oldContent.trim()) {
        return paragraph; // Keep empty paragraphs as-is
      }
      return `<p><span style="font-family:${fontFamilyString};font-size:${fontSize}px">${oldContent}</span></p>`;
    });
  }

  __addFontStylesToTables(content, fontFamily, fontSize) {
    const fontFamilyString = fontFamily.includes(' ')
      ? `'${fontFamily}'`
      : fontFamily;

    return content.replace(
      /<(td|th)>(.*?)<\/\1>/g,
      (match, tag, oldContent) =>
        `<${tag}><span style="font-family:${fontFamilyString};font-size:${fontSize}px">${oldContent}</span></${tag}>`,
    );
  }

  __addFontStylesToListItems(content, fontFamily, fontSize) {
    const fontFamilyString = fontFamily.includes(' ')
      ? `'${fontFamily}'`
      : fontFamily;

    return content.replace(
      /<li>(.*?)<\/li>/g,
      (match, oldContent) =>
        `<li><span style="font-family:${fontFamilyString};font-size:${fontSize}px">${oldContent}</span></li>`,
    );
  }

  __ensureFontStyling(content) {
    if (!content || typeof content !== 'string') {
      return content;
    }

    if (
      content.includes('style="font-family:') ||
      content.includes("style='font-family:")
    ) {
      return content;
    }

    return this.__applyFontStyling(content);
  }

  async __saveNotesWithLogging(parsedNotes, encounterId) {
    try {
      const prevNotes = await fetchNotes(
        {
          subjective: '',
          objective: '',
          assessment: '',
          plan: '',
        },
        encounterId,
      );

      const mergedNotes = this.__mergeNotes(prevNotes, parsedNotes);

      const saveResults = await saveNotes(mergedNotes, encounterId);

      if (saveResults) {
        store.dispatch(openSuccess('Chart notes synced successfully'));
        console.log(
          `[AMBIENT SCRIBE] Notes saved successfully for encounter ${encounterId}`,
        );
      }

      return saveResults;
    } catch (error) {
      console.error('[AMBIENT SCRIBE] Save failed:', error.message);
      store.dispatch(openError('Error when syncing chart notes'));
      throw error;
    }
  }

  __notifyDocumentExported(data) {
    updated({
      ambientScribe: {
        event: 'document-exported',
        document: data.document,
        encounterId: this.pendingContext.encounterId,
        timestamp: Date.now(),
      },
    });
  }

  // State getters
  get isWidgetMounted() {
    return this.widget.isMounted();
  }

  get isWidgetLoaded() {
    return this.widgetLoaded;
  }

  get currentContext() {
    return this.pendingContext;
  }
}
